import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { FileSignature, Plus, Search } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useAuth } from 'contexts/AuthContext';
import { listVendorContracts, VendorContractListItem, VENDOR_TYPE_LABELS } from './api';
import StatusBadge from './StatusBadge';

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'approved', label: 'Approved' },
  { value: 'executed', label: 'Executed' },
  { value: 'expired', label: 'Expired' },
];
const PAGE_SIZES = [10, 20, 50];
const COLUMNS = 7;

const fmtDate = (d: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : '—');

const VendorContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const perms = useAuth().userInfo?.perms ?? [];
  const canCreate = perms.includes('vendor_contracts.create');
  const canApprove = perms.includes('vendor_contracts.approve');

  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([{ label: 'Contracts', href: '/contracts' }, { label: 'Vendor Contracts' }]);
  }, [setBreadcrumbs]);

  const [rows, setRows] = useState<VendorContractListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'all' | 'mine'>('all');   // 'mine' = awaiting my approval
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [vendorType, setVendorType] = useState('all');
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    try {
      const data = await listVendorContracts({
        search, status: view === 'all' && status !== 'all' ? status : undefined,
        vendorType: vendorType === 'all' ? undefined : vendorType, awaitingMe: view === 'mine', limit, offset,
      });
      setRows(data.contracts);
      setTotal(data.total);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load vendor contracts');
    } finally {
      setLoaded(true);
    }
  }, [search, status, vendorType, view, limit, offset]);

  // debounce typing in the search box; filter / page changes go immediately
  const typing = useRef(false);
  useEffect(() => {
    const t = setTimeout(load, typing.current ? 250 : 0);
    typing.current = false;
    return () => clearTimeout(t);
  }, [load]);

  const filtered = !!search || status !== 'all' || vendorType !== 'all' || view === 'mine';
  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="flex flex-col gap-4 px-4 sm:px-6 pb-6">
      {canApprove && (
        <div className="flex gap-1 border-b">
          {([['all', 'All contracts'], ['mine', 'Awaiting my approval']] as const).map(([v, label]) => (
            <button
              key={v}
              onClick={() => { setView(v); setOffset(0); }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${view === v ? 'border-blue-600 font-medium' : 'border-transparent text-muted-foreground'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[220px] max-w-sm flex-1">
          <label className="mb-1 block text-sm font-medium text-gray-700">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Agreement no., vendor, PAN, route…"
              value={search}
              onChange={(e) => { typing.current = true; setSearch(e.target.value); setOffset(0); }}
              className="pl-9"
            />
          </div>
        </div>
        {view === 'all' && (
          <div className="w-44">
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <Select value={status} onValueChange={(v) => { setStatus(v); setOffset(0); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="w-44">
          <label className="mb-1 block text-sm font-medium text-gray-700">Vendor Type</label>
          <Select value={vendorType} onValueChange={(v) => { setVendorType(v); setOffset(0); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(VENDOR_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {canCreate && (
          <Button className="ml-auto h-10" onClick={() => navigate('/contracts/vendor/new')}>
            <Plus className="mr-1 h-4 w-4" /> New contract
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border bg-card">
        <Table className="[&_td]:px-3 [&_td]:py-1.5 [&_th]:h-auto [&_th]:px-3 [&_th]:py-2">
          <TableHeader className="bg-muted/50 [&_th]:whitespace-nowrap [&_th]:font-semibold [&_th]:text-foreground">
            <TableRow className="hover:bg-transparent">
              <TableHead>Agreement No.</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Lanes</TableHead>
              <TableHead>Validity</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loaded && Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`sk${i}`} className="hover:bg-transparent">
                {Array.from({ length: COLUMNS }).map((__, j) => (
                  <TableCell key={j}><Skeleton className="h-4 w-full max-w-[140px]" /></TableCell>
                ))}
              </TableRow>
            ))}
            {loaded && rows.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={COLUMNS} className="h-32 text-center">
                  <div className="flex flex-col items-center gap-1 text-muted-foreground">
                    <FileSignature className="h-6 w-6 opacity-50" />
                    <p className="text-sm">
                      {view === 'mine' ? 'Nothing is waiting for your approval' : filtered ? 'No vendor contracts match these filters' : 'No vendor contracts yet'}
                    </p>
                    {!filtered && canCreate && <p className="text-xs">Create one with “New contract”</p>}
                  </div>
                </TableCell>
              </TableRow>
            )}
            {rows.map((c) => (
              <TableRow key={c.id} className="cursor-pointer font-normal hover:bg-muted/40" onClick={() => navigate(`/contracts/vendor/${c.id}`)}>
                <TableCell className="whitespace-nowrap font-medium">{c.contract_number || `#${c.id}`}</TableCell>
                <TableCell>{c.vendor_name}</TableCell>
                <TableCell className="whitespace-nowrap">{VENDOR_TYPE_LABELS[c.vendor_type] || c.vendor_type} · {c.load_type}</TableCell>
                <TableCell className="max-w-[260px]">
                  <span className="line-clamp-1" title={c.first_route || ''}>{c.first_route || '—'}</span>
                  {c.lane_count > 1 && <span className="text-xs text-muted-foreground">+{c.lane_count - 1} more</span>}
                </TableCell>
                <TableCell className="whitespace-nowrap">{fmtDate(c.valid_from)} – {fmtDate(c.valid_to)}</TableCell>
                <TableCell><StatusBadge status={c.status} /></TableCell>
                <TableCell className="whitespace-nowrap">{c.created_by_name || c.created_by}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {loaded && total > 0 && (
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <div>Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} contracts</div>
            <div className="flex items-center gap-2">
              <span>Rows per page:</span>
              <select
                className="h-8 w-16 rounded border border-input bg-background px-2 text-sm"
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}
              >
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}>Previous</Button>
            <div className="flex items-center gap-1 text-sm">
              <span>Page</span><span className="font-medium">{page}</span><span>of</span><span className="font-medium">{pageCount}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorContractsPage;
